<template>
  <SrpModal :isVisible="props.isVisible" :isClosable="false" size="large" width="980" :zIndex="100" @update:isVisible="value => emit('update:isVisible', value)">
    <template #content>
      <div class="social-account-connect-prep__content-title">
        <h2 class="global-h2">Start connecting your accounts</h2>
      </div>

      <section class="social-account-connect-prep__section">
        <p class="global-p">These authentication flows can be confusing, but we're here to help!</p>
        <!--<h3 class="global-h4">Which accounts would you like to connect?</h3>-->
        <div class="social-account-connect-prep__platform-grid">
          <button
            v-for="platform in SUPPORTED_PLATFORMS"
            :key="platform"
            type="button"
            :class="{
              'social-account-connect-prep__platform-card': true,
              'social-account-connect-prep__platform-card--selected': selectedPlatforms.includes(platform),
            }"
            @click="togglePlatform(platform)"
          >
            <div v-if="connectedPlatformsSet.has(platform)" class="social-account-connect-prep__connected-badge">Connected</div>
            <span
              v-else
              class="social-account-connect-prep__platform-checkbox"
              :class="{ 'social-account-connect-prep__platform-checkbox--checked': selectedPlatforms.includes(platform) }"
              aria-hidden="true"
            >
              <IconEmbedded v-if="selectedPlatforms.includes(platform)" name="check_3-5" :size="12" color="teal" />
            </span>
            <img
              class="social-account-connect-prep__platform-logo"
              :class="{
                'social-account-connect-prep__platform-logo--inactive': !selectedPlatforms.includes(platform) && !connectedPlatformsSet.has(platform),
              }"
              :src="`/images/social-logo-${platform}.svg`"
              :alt="getSocialChannelLabel(platform)"
            />
            <div class="social-account-connect-prep__platform-name">{{ getSocialChannelLabel(platform) }}</div>
          </button>
        </div>
      </section>

      <section class="social-account-connect-prep__section">
        <h3 class="global-h4">Before you connect:</h3>

        <div class="social-account-connect-prep__tips-stack">
          <Transition name="prep-tip">
            <div v-if="selectedPlatforms.includes('instagram')" key="tip-instagram" class="social-account-connect-prep__tip-enter-wrap">
              <div class="social-account-connect-prep__tip-card">
                <div class="social-account-connect-prep__tip-card-title-row">
                  <img class="social-account-connect-prep__tip-card-icon" src="/images/social-logo-instagram.svg" alt="" width="22" height="22" />
                  <h4 class="global-h5 social-account-connect-prep__tip-card-title">Instagram tips</h4>
                </div>
                <ul class="social-account-connect-prep__tip-list">
                  <li>Your Instagram account must be a <b>Business or Creator Profile</b>.</li>
                  <li>If you use Facebook login, your Instagram needs to be connected in Facebook.</li>
                </ul>
                <h5 class="social-account-connect-prep__login-options-heading">Login options</h5>
                <ul class="social-account-connect-prep__tip-list social-account-connect-prep__login-options-list">
                  <li><b>Instagram Direct:</b> use your Instagram username/password, often simpler.</li>
                  <li><b>Facebook Login:</b> login through Facebook, often useful if Facebook and Instagram are already linked.</li>
                </ul>
              </div>
            </div>
          </Transition>

          <Transition name="prep-tip">
            <div v-if="selectedPlatforms.includes('facebook')" key="tip-facebook" class="social-account-connect-prep__tip-enter-wrap">
              <div class="social-account-connect-prep__tip-card">
                <div class="social-account-connect-prep__tip-card-title-row">
                  <img class="social-account-connect-prep__tip-card-icon" src="/images/social-logo-facebook.svg" alt="" width="22" height="22" />
                  <h4 class="global-h5 social-account-connect-prep__tip-card-title">Facebook tips</h4>
                </div>
                <ul class="social-account-connect-prep__tip-list">
                  <li>You must be an <b>Admin of the Facebook Page</b> you want to connect.</li>
                  <li>Only <b>Business Pages</b> will show (personal Facebook Pages cannot be linked).</li>
                  <li>Make sure you sign in with the Facebook login with access to the your Page.</li>
                </ul>
              </div>
            </div>
          </Transition>

          <Transition name="prep-tip">
            <div v-if="selectedPlatforms.includes('tiktok')" key="tip-tiktok" class="social-account-connect-prep__tip-enter-wrap">
              <div class="social-account-connect-prep__tip-card">
                <div class="social-account-connect-prep__tip-card-title-row">
                  <img class="social-account-connect-prep__tip-card-icon" src="/images/social-logo-tiktok.svg" alt="" width="22" height="22" />
                  <h4 class="global-h5 social-account-connect-prep__tip-card-title">TikTok tips</h4>
                </div>
                <ul class="social-account-connect-prep__tip-list">
                  <li>Sign in with your TikTok username and password, usually pretty simple.</li>
                </ul>
              </div>
            </div>
          </Transition>
        </div>
        <p v-if="!hasWhatToKnowTips" class="global-p social-account-connect-prep__tips-placeholder">Select a network for common connection issues and tips.</p>
      </section>

      <div class="social-account-connect-prep__divider" />

      <section class="social-account-connect-prep__section social-account-connect-prep__trouble-section">
        <h3 class="global-h4">Trouble connecting?</h3>
        <div class="social-account-connect-prep__support-row">
          Reach out anytime, we're happy to help!
          <CopyTextNew color="rgba(0, 0, 0, 0.87)">support@shrpa.com</CopyTextNew>
        </div>

        <SrpButton v-if="!isWhatWentWrongExpanded" class="social-account-connect-prep__let-us-know-toggle" size="tiny" fill="outlined" color="gray" @click="isWhatWentWrongExpanded = true">
          Report an issue
        </SrpButton>

        <div v-else class="social-account-connect-prep__issue-wrap">
          <div class="global-p" style="margin: 0 0 8px"><b>What went wrong?</b></div>
          <div class="social-account-connect-prep__issue-chip-list">
            <button
              v-for="issue in ISSUE_OPTIONS"
              :key="issue.id"
              type="button"
              :class="{
                'social-account-connect-prep__issue-chip': true,
                'social-account-connect-prep__issue-chip--selected': selectedIssueId === issue.id,
              }"
              @click="selectedIssueId = issue.id"
            >
              {{ issue.label }}
            </button>
          </div>
          <div v-if="selectedIssueHelpText" class="social-account-connect-prep__issue-help-row">
            <p class="global-p social-account-connect-prep__issue-help"><b>Potential fix:</b> {{ selectedIssueHelpText }}</p>
            <SrpButton
              v-if="selectedIssueId === 'facebook-error'"
              class="social-account-connect-prep__standard-scope-retry"
              size="tiny"
              :isDisabled="props.isRedirecting || isSubmittingStandardScopeRetry"
              @click="handleFacebookStandardScopeRetry"
            >
              <template #iconRight>
                <LoadingIcon v-if="isSubmittingStandardScopeRetry" :size="18" />
                <IconEmbedded v-else name="arrow-right_3-5" :size="14" />
              </template>
              Use Standard Scope
            </SrpButton>
          </div>

          <div v-if="selectedIssueId === 'other'" class="social-account-connect-prep__other-wrap">
            <textarea v-model="otherIssueText" class="global-text-input social-account-connect-prep__other-textarea" rows="3" placeholder="Tell us what happened..." />
          </div>

          <div class="social-account-connect-prep__feedback-actions">
            <SrpButton size="small" fill="outlined" :isDisabled="!canSubmitLetShrpaKnowFeedback || isSubmittingIssueReport || isIssueReportSubmitted" @click="handleLetShrpaKnowClick">
              Let Shrpa know
            </SrpButton>
            <span v-if="isIssueReportSubmitted" class="social-account-connect-prep__other-saved">Issue reported, thanks!</span>
          </div>
        </div>
      </section>
    </template>

    <template #footer>
      <SrpButton color="gray" fill="outlined" :isDisabled="props.isRedirecting" @click="emit('update:isVisible', false)">Back</SrpButton>
      <SrpButton :isDisabled="props.isRedirecting" @click="handleConnectClick">
        <template #iconRight>
          <LoadingIcon v-if="props.isRedirecting" :size="18" />
          <IconEmbedded v-else name="arrow-right_3-5" :size="18" />
        </template>
        {{ props.isRedirecting ? "Redirecting..." : "Next" }}
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { getSocialChannelLabel } from "@contracts/socialNetworks";
import { getCalendarId } from "@logic/calendarManager";
import SocialIntegrationRepo from "@repos/SocialIntegrationRepo";

import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import CopyTextNew from "@components/CopyTextNew.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

type PrepPlatform = "facebook" | "instagram" | "tiktok";
type IssueOptionId = "missing-page" | "oauth-error" | "facebook-error" | "other";

const SUPPORTED_PLATFORMS: PrepPlatform[] = ["instagram", "facebook", "tiktok"];
const ISSUE_OPTIONS: Array<{ id: IssueOptionId; label: string; helper: string | null }> = [
  { id: "missing-page", label: "My Page wasn't listed", helper: "Confirm you are the Page admin and logged into the right account." },
  { id: "oauth-error", label: '"OAuth error" message', helper: "Try the same flow again (it often works the second time)." },
  { id: "facebook-error", label: "Error in Facebook", helper: "Try again with Facebook Standard Scope." },
  { id: "other", label: "Other", helper: null },
];

function prepPlatformsStorageKey(customerId: string, calendarId: string): string {
  return `shrpa.calendar.${customerId}.connectPrepPlatforms.${calendarId}`;
}

function loadPrepPlatformsFromStorage(customerId: string, calendarId: string, connectedLowercase: ReadonlySet<string>): Array<PrepPlatform> {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(prepPlatformsStorageKey(customerId, calendarId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p): p is PrepPlatform => typeof p === "string" && SUPPORTED_PLATFORMS.includes(p as PrepPlatform)).filter(p => !connectedLowercase.has(p));
  } catch {
    return [];
  }
}

function savePrepPlatformsToStorage(customerId: string, calendarId: string, platforms: Array<PrepPlatform>): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(prepPlatformsStorageKey(customerId, calendarId), JSON.stringify(platforms));
  } catch {
    // ignore quota / private mode
  }
}

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    customerId: string;
    isRedirecting?: boolean;
    connectedPlatforms?: Array<string>;
  }>(),
  {
    isVisible: false,
    isRedirecting: false,
    connectedPlatforms: () => [],
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
  (e: "confirm", payload: { selectedPlatforms: Array<PrepPlatform>; forceStandardScope?: boolean }): void;
}>();

const selectedPlatforms = ref<Array<PrepPlatform>>([]);
const selectedIssueId = ref<IssueOptionId | "">("");
const otherIssueText = ref("");
/** After a successful "Let Shrpa know" submit: show thanks and keep the button disabled until issue selection changes. */
const isIssueReportSubmitted = ref(false);
const isSubmittingIssueReport = ref(false);
const isSubmittingStandardScopeRetry = ref(false);
const isWhatWentWrongExpanded = ref(false);
/** Set when the modal is open and the active calendar id for this customer has been resolved (used for localStorage key). */
const prepCalendarIdForStorage = ref<string | null>(null);

const connectedPlatformsSet = computed(() => new Set(props.connectedPlatforms.map(v => v.toLowerCase())));

const selectedIssueHelpText = computed(() => {
  return ISSUE_OPTIONS.find(i => i.id === selectedIssueId.value)?.helper || "";
});

/** True when an issue chip is selected and, for Other, non-empty trimmed text. */
const canSubmitLetShrpaKnowFeedback = computed(() => {
  const id = selectedIssueId.value;
  if (!id) return false;
  if (id === "other") return otherIssueText.value.trim().length > 0;
  return true;
});

const hasWhatToKnowTips = computed(() => SUPPORTED_PLATFORMS.some(p => selectedPlatforms.value.includes(p)));

watch([selectedIssueId, otherIssueText], () => {
  isIssueReportSubmitted.value = false;
});

watch(
  () => props.isVisible,
  async isVisible => {
    if (!isVisible) {
      prepCalendarIdForStorage.value = null;
      return;
    }
    selectedIssueId.value = "";
    otherIssueText.value = "";
    isIssueReportSubmitted.value = false;
    isWhatWentWrongExpanded.value = false;
    try {
      const calendarId = await getCalendarId(props.customerId);
      if (!props.isVisible) return;
      prepCalendarIdForStorage.value = calendarId;
      const connected = new Set(props.connectedPlatforms.map(v => v.toLowerCase()));
      selectedPlatforms.value = loadPrepPlatformsFromStorage(props.customerId, calendarId, connected);
    } catch {
      if (!props.isVisible) return;
      prepCalendarIdForStorage.value = null;
      selectedPlatforms.value = [];
    }
  },
  { immediate: true }
);

watch(
  selectedPlatforms,
  platforms => {
    if (!props.isVisible || !prepCalendarIdForStorage.value) return;
    savePrepPlatformsToStorage(props.customerId, prepCalendarIdForStorage.value, [...platforms]);
  },
  { deep: true }
);

function togglePlatform(platform: PrepPlatform) {
  if (selectedPlatforms.value.includes(platform)) {
    selectedPlatforms.value = selectedPlatforms.value.filter(p => p !== platform);
    return;
  }
  selectedPlatforms.value = [...selectedPlatforms.value, platform];
}

function handleConnectClick() {
  emit("confirm", { selectedPlatforms: selectedPlatforms.value });
}

async function handleLetShrpaKnowClick() {
  if (!canSubmitLetShrpaKnowFeedback.value || isSubmittingIssueReport.value) return;
  const id = selectedIssueId.value;
  if (!id) return;
  isSubmittingIssueReport.value = true;
  await SocialIntegrationRepo.reportConnectPrepIssue(props.customerId, {
    issueId: id,
    issueText: id === "other" ? otherIssueText.value.trim() : "",
    selectedPlatforms: selectedPlatforms.value,
  });
  isIssueReportSubmitted.value = true;
  isSubmittingIssueReport.value = false;
}

async function handleFacebookStandardScopeRetry() {
  if (props.isRedirecting || isSubmittingStandardScopeRetry.value) return;
  isSubmittingStandardScopeRetry.value = true;
  await SocialIntegrationRepo.reportConnectPrepIssue(props.customerId, {
    issueId: "facebook-error",
    issueText: "",
    selectedPlatforms: selectedPlatforms.value,
    clickedStandardScopeRetry: true,
  });
  emit("confirm", { selectedPlatforms: selectedPlatforms.value, forceStandardScope: true });
}
</script>

<style scoped lang="scss">
.social-account-connect-prep {
  &__content-title {
    margin-bottom: 12px;

    .global-h2 {
      margin-bottom: 0;
    }
  }

  &__section {
    margin-bottom: 24px;
  }

  &__divider {
    margin-bottom: 35px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__tips-placeholder {
    margin-top: 8px;
    margin-bottom: 0;
    color: rgba(0, 0, 0, 0.55);
  }

  &__platform-grid {
    gap: 12px;
    margin-top: 10px;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  &__platform-card {
    padding: 14px 12px 12px;
    border: 1px solid rgba(17, 134, 137, 0.2);
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    background: #fff;
    cursor: pointer;
    transition:
      border-color 0.12s ease-in-out,
      box-shadow 0.12s ease-in-out;

    &:hover {
      border-color: rgba(17, 134, 137, 0.45);
    }

    &--selected {
      border-color: rgba(17, 134, 137, 1);
      box-shadow: 0 0 0 1px rgba(17, 134, 137, 0.25) inset;
    }
  }

  &__platform-checkbox {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    border: 2px solid rgba(0, 0, 0, 0.28);
    background: #fff;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    inset: 8px 8px auto auto;
    z-index: 1;
    flex-shrink: 0;
    box-sizing: border-box;
    transition:
      border-color 0.12s ease-in-out,
      background-color 0.12s ease-in-out;

    &--checked {
      border-color: rgba(17, 134, 137, 1);
      background: #fff;
    }
  }

  &__platform-logo {
    width: 30px;
    height: 30px;
    margin-top: 8px;
    margin-bottom: 10px;
    transition:
      filter 0.12s ease-in-out,
      opacity 0.12s ease-in-out;

    &--inactive {
      filter: grayscale(1);
      opacity: 0.5;
    }
  }

  &__platform-name {
    font-size: 14px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.78);
    letter-spacing: 0.01em;
  }

  &__connected-badge {
    position: absolute;
    inset: 8px 8px auto auto;
    z-index: 1;
    padding: 2px 8px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    color: #0f6f72;
    background: rgba(17, 134, 137, 0.1);
  }

  &__tips-stack {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  &__tip-enter-wrap {
    overflow: hidden;
    will-change: max-height, opacity, transform;
  }

  &__tip-enter-wrap.prep-tip-enter-active,
  &__tip-enter-wrap.prep-tip-leave-active {
    transition:
      max-height 0.38s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.26s ease,
      transform 0.26s ease;
  }

  &__tip-enter-wrap.prep-tip-enter-from,
  &__tip-enter-wrap.prep-tip-leave-to {
    max-height: 0;
    opacity: 0;
    transform: translateY(-8px);
  }

  &__tip-enter-wrap.prep-tip-enter-to,
  &__tip-enter-wrap.prep-tip-leave-from {
    max-height: 28rem;
    opacity: 1;
    transform: translateY(0);
  }

  &__tip-card {
    padding: 12px 14px;
    margin-top: 0;
    border: 1px solid rgba(17, 134, 137, 0.2);
    border-radius: 10px;
    background: rgba(17, 134, 137, 0.03);
    color: rgba(0, 0, 0, 0.87);

    .global-h5 {
      color: rgba(0, 0, 0, 0.87);
    }
  }

  &__tip-card-title-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__tip-card-icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    object-fit: contain;
  }

  &__tip-card-title {
    margin: 0;
  }

  &__tip-list {
    margin: 10px 0 0 18px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__login-options-heading {
    margin: 16px 0 6px;
    padding: 0;
    color: rgba(0, 0, 0, 0.87);
    text-decoration: underline;
    text-underline-offset: 4px;
  }

  &__login-options-list {
    margin-top: 0;
  }

  &__trouble-section {
    margin-bottom: 0;
    color: rgba(0, 0, 0, 0.87);

    .global-h4 {
      color: rgba(0, 0, 0, 0.87);
    }
  }

  &__support-row {
    gap: 8px;
    margin: 8px 0 14px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__let-us-know-toggle {
    align-self: flex-start;
  }

  &__feedback-actions {
    margin-top: 12px;
    gap: 10px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  &__issue-wrap {
    padding: 12px 14px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    background: rgba(248, 248, 248, 1);
  }

  &__issue-chip-list {
    gap: 8px;
    display: flex;
    flex-wrap: wrap;
  }

  &__issue-chip {
    height: 33px;
    padding: 0 12px 2px;
    border: 1px solid rgba(17, 134, 137, 0.25);
    border-radius: 30px;
    color: rgba(0, 0, 0, 0.87);
    font:
      600 14px/14px "Quicksand",
      sans-serif;
    background: #fff;
    cursor: pointer;

    &--selected {
      border-color: rgba(17, 134, 137, 1);
      background: rgba(17, 134, 137, 0.08);
    }
  }

  &__issue-help-row {
    margin-top: 9px;
    gap: 10px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  &__issue-help {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.87);
  }

  &__standard-scope-retry {
    flex-shrink: 0;
  }

  &__other-wrap {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  &__other-textarea {
    width: 100%;
    min-height: 84px;
    padding: 10px 11px;
  }

  &__other-saved {
    font-size: 13px;
    font-weight: 700;
  }
}

@media (max-width: 767px) {
  .social-account-connect-prep {
    &__platform-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
